import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, Lock, AlertCircle, Zap } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const { signIn, isDemoMode } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { error: authError } = await signIn(email, password);
      if (authError) setError('Ungueltige Anmeldedaten.');
      else navigate('/app');
    } catch { setError('Anmeldung fehlgeschlagen.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{background: 'linear-gradient(135deg, #007D88 0%, #18DDEF 100%)'}}>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20xmlns=%22http://www.w3.org/2000/svg%22%20width=%22100%22%20height=%22100%22%3E%3Cg%20fill=%22%23ffffff%22%20fill-opacity=%220.04%22%3E%3Cpath%20d=%22M0%200h100v100H0z%22/%3E%3Cpath%20d=%22M0%200l50%2050M50%200l50%2050M0%2050l50%2050M50%2050l50%2050%22%20stroke=%22%23ffffff%22%20stroke-opacity=%220.08%22%20stroke-width=%221%22%20fill=%22none%22/%3E%3C/g%3E%3C/svg%3E')]" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <Zap size={24} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-bold text-xl">AIQCON</div>
              <div className="text-xs opacity-80 uppercase tracking-wider">Energy Broker Platform</div>
            </div>
          </div>

          <div>
            <h1 className="text-4xl font-bold leading-tight mb-4">
              Professionelle<br />
              Energiebeschaffung<br />
              fuer den Mittelstand
            </h1>
            <p className="text-white/80 text-base leading-relaxed max-w-md">
              Ausschreibung, Angebot, Vertrag — alles in einer Plattform. 10+ Versorger, transparent und effizient.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-md">
            <div>
              <div className="text-3xl font-bold">500+</div>
              <div className="text-xs text-white/70 uppercase tracking-wider">Kunden</div>
            </div>
            <div>
              <div className="text-3xl font-bold">15%</div>
              <div className="text-xs text-white/70 uppercase tracking-wider">Ersparnis</div>
            </div>
            <div>
              <div className="text-3xl font-bold">10+</div>
              <div className="text-xs text-white/70 uppercase tracking-wider">Versorger</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Zap className="text-primary-foreground" size={20} strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-bold text-lg">AIQCON</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider">Energy Broker</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-foreground">Willkommen zurueck</h1>
          <p className="text-muted-foreground text-sm mt-2">Melden Sie sich in Ihrem Konto an</p>

          {isDemoMode && (
            <div className="mt-6 p-3 bg-accent border border-primary/20 rounded-lg text-sm text-primary">
              Melden Sie sich mit Ihren AIQCON-Zugangsdaten an.
            </div>
          )}

          {error && (
            <div className="mt-6 p-3 bg-[#ffe6e6] border border-destructive/20 rounded-lg text-sm text-destructive flex items-center gap-2">
              <AlertCircle size={16} /><span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div>
              <label className="text-sm font-semibold text-foreground mb-2 block">E-Mail</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="name@aiqcon.com" required className="w-full pl-11 pr-4 py-3 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-foreground">Passwort</label>
                <a href="#" className="text-xs text-primary font-medium hover:underline">Passwort vergessen?</a>
              </div>
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Passwort eingeben" required className="w-full pl-11 pr-4 py-3 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-primary text-primary-foreground py-3 rounded-lg text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 shadow-sm shadow-primary/20">
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </button>
          </form>

          <p className="text-xs text-muted-foreground text-center mt-8">
            &copy; {new Date().getFullYear()} AIQCON GmbH &middot; Alle Rechte vorbehalten
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
